/** 카메라 배치 도면: 정규좌표(0–100) ↔ 캔버스, 실측 비율 유지 */

export type PoolViewport = {
  offsetX: number;
  offsetY: number;
  drawW: number;
  drawH: number;
};

export function poolPolygonBounds(poly: number[][]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} {
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/** lengthM : widthM 비율로 캔버스 안에 시설 도면 영역 배치 */
export type ViewMargins = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

export type DeckEdgeMeters = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

/** 풀 + 벽·풀(데크) 여백을 포함한 도면 실측 크기 */
export function facilityWorldSizeM(
  poolLengthM: number,
  poolWidthM: number,
  edges: DeckEdgeMeters,
): { lengthM: number; widthM: number } {
  const left = Math.max(0, Number(edges.left) || 0);
  const right = Math.max(0, Number(edges.right) || 0);
  const top = Math.max(0, Number(edges.top) || 0);
  const bottom = Math.max(0, Number(edges.bottom) || 0);
  return {
    lengthM: Math.max(0.1, poolLengthM) + left + right,
    widthM: Math.max(0.1, poolWidthM) + top + bottom,
  };
}

/**
 * 풀 정규 bbox를 데크(m)만큼 바깥으로 확장.
 * 벽캠·치수선이 풀 밖에 있어도 viewBounds 안에 들어오게 한다.
 */
export function expandPoolBoundsByDeckMeters(
  poolBounds: { minX: number; maxX: number; minY: number; maxY: number },
  poolLengthM: number,
  poolWidthM: number,
  edges: DeckEdgeMeters,
): { minX: number; maxX: number; minY: number; maxY: number } {
  const spanX = Math.max(0.01, poolBounds.maxX - poolBounds.minX);
  const spanY = Math.max(0.01, poolBounds.maxY - poolBounds.minY);
  const L = Math.max(0.1, poolLengthM);
  const W = Math.max(0.1, poolWidthM);
  const left = Math.max(0, Number(edges.left) || 0);
  const right = Math.max(0, Number(edges.right) || 0);
  const top = Math.max(0, Number(edges.top) || 0);
  const bottom = Math.max(0, Number(edges.bottom) || 0);
  return {
    minX: poolBounds.minX - (left / L) * spanX,
    maxX: poolBounds.maxX + (right / L) * spanX,
    minY: poolBounds.minY - (top / W) * spanY,
    maxY: poolBounds.maxY + (bottom / W) * spanY,
  };
}

/** 치수 라벨용 캔버스 여백(px) */
const FACILITY_LABEL_MARGINS = { top: 52, right: 76, bottom: 58, left: 84 };
/** 데크 포함 도면이 화면 안에 들어오도록 하는 최소·최대 m당 픽셀 */
const MIN_PX_PER_M = 22;
const MAX_PX_PER_M = 52;

/**
 * 컨테이너 폭 + 풀·데크 실측으로 캔버스 비트맵 크기를 가변 산출.
 * 데크가 크면 가로·세로가 함께 늘어나 벽캠이 잘리지 않는다.
 */
export function computeFacilityCanvasLayout(
  poolLengthM: number,
  poolWidthM: number,
  edges: DeckEdgeMeters,
  containerWidthPx: number,
): {
  canvasW: number;
  canvasH: number;
  margins: Required<ViewMargins>;
  worldLengthM: number;
  worldWidthM: number;
  fitScale: number;
} {
  const { lengthM: worldLengthM, widthM: worldWidthM } = facilityWorldSizeM(
    poolLengthM,
    poolWidthM,
    edges,
  );
  const margins: Required<ViewMargins> = { ...FACILITY_LABEL_MARGINS };
  const hostW = Math.max(480, Number(containerWidthPx) || 960);
  const innerFromHost = Math.max(200, hostW - margins.left - margins.right);
  let ppm = innerFromHost / worldLengthM;
  if (ppm < MIN_PX_PER_M) ppm = MIN_PX_PER_M;
  if (ppm > MAX_PX_PER_M) ppm = MAX_PX_PER_M;
  const innerW = worldLengthM * ppm;
  const innerH = worldWidthM * ppm;
  const canvasW = Math.round(
    Math.min(2400, Math.max(hostW, innerW + margins.left + margins.right)),
  );
  // 실제 반영 ppm(가로 맞춤 후)
  const ppmDraw = (canvasW - margins.left - margins.right) / worldLengthM;
  const canvasH = Math.round(
    Math.min(2000, Math.max(420, worldWidthM * ppmDraw + margins.top + margins.bottom)),
  );
  return {
    canvasW,
    canvasH,
    margins,
    worldLengthM,
    worldWidthM,
    /** 데크가 viewBounds에 포함되므로 풀만 축소할 필요 적음 */
    fitScale: 0.96,
  };
}

export function getPoolViewport(
  canvasW: number,
  canvasH: number,
  lengthM: number,
  widthM: number,
  padding = 20,
  margins?: ViewMargins,
): PoolViewport {
  const top = margins?.top ?? padding;
  const right = margins?.right ?? padding;
  const bottom = margins?.bottom ?? padding;
  const left = margins?.left ?? padding;
  const innerW = Math.max(40, canvasW - left - right);
  const innerH = Math.max(40, canvasH - top - bottom);
  const aspect = Math.max(0.1, lengthM) / Math.max(0.1, widthM);
  let drawW = innerW;
  let drawH = innerH;
  if (drawW / drawH > aspect) {
    drawW = drawH * aspect;
  } else {
    drawH = drawW / aspect;
  }
  return {
    offsetX: left + (innerW - drawW) / 2,
    offsetY: top + (innerH - drawH) / 2,
    drawW,
    drawH,
  };
}

export function normToCanvas(px: number, py: number, vp: PoolViewport): { x: number; y: number } {
  return {
    x: vp.offsetX + (px / 100) * vp.drawW,
    y: vp.offsetY + (py / 100) * vp.drawH,
  };
}

/** 풀 bbox → draw 영역. fitScale&lt;1 이면 도면 안에서 풀만 약간 축소(외곽 공백) */
export function normToCanvasInBounds(
  px: number,
  py: number,
  vp: PoolViewport,
  bounds: ReturnType<typeof poolPolygonBounds>,
  fitScale = 1,
): { x: number; y: number } {
  const spanX = Math.max(0.01, bounds.maxX - bounds.minX);
  const spanY = Math.max(0.01, bounds.maxY - bounds.minY);
  const u = (px - bounds.minX) / spanX;
  const v = (py - bounds.minY) / spanY;
  const s = Math.max(0.72, Math.min(1, fitScale));
  return {
    x: vp.offsetX + vp.drawW * (0.5 + (u - 0.5) * s),
    y: vp.offsetY + vp.drawH * (0.5 + (v - 0.5) * s),
  };
}

export function canvasToNorm(cx: number, cy: number, vp: PoolViewport): { x: number; y: number } {
  const x = ((cx - vp.offsetX) / vp.drawW) * 100;
  const y = ((cy - vp.offsetY) / vp.drawH) * 100;
  return {
    x: Math.round(x * 10) / 10,
    y: Math.round(y * 10) / 10,
  };
}

export function canvasToNormInBounds(
  cx: number,
  cy: number,
  vp: PoolViewport,
  bounds: ReturnType<typeof poolPolygonBounds>,
  fitScale = 1,
): { x: number; y: number } {
  const spanX = Math.max(0.01, bounds.maxX - bounds.minX);
  const spanY = Math.max(0.01, bounds.maxY - bounds.minY);
  const s = Math.max(0.72, Math.min(1, fitScale));
  const u = ((cx - vp.offsetX) / vp.drawW - 0.5) / s + 0.5;
  const v = ((cy - vp.offsetY) / vp.drawH - 0.5) / s + 0.5;
  return {
    x: Math.round((bounds.minX + u * spanX) * 10) / 10,
    y: Math.round((bounds.minY + v * spanY) * 10) / 10,
  };
}

/** 실측 비율에 맞는 기본 풀 사각형(정규좌표), marginRatio=0.03 → 약 3% 여백 */
export function defaultPoolPolygonRect(marginRatio = 0.035): number[][] {
  const m = Math.max(0, Math.min(0.12, marginRatio)) * 100;
  return [
    [m, m],
    [100 - m, m],
    [100 - m, 100 - m],
    [m, 100 - m],
  ];
}

export function isNormInsidePoolBounds(
  x: number,
  y: number,
  bounds: ReturnType<typeof poolPolygonBounds>,
): boolean {
  return x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY;
}

/** 도면 치수선 (extension line + dimension line + 화살표 + 텍스트) */
export function drawHorizontalDimension(
  ctx: CanvasRenderingContext2D,
  xLeft: number,
  xRight: number,
  yAnchor: number,
  yDim: number,
  label: string,
) {
  ctx.save();
  ctx.strokeStyle = '#455a64';
  ctx.fillStyle = '#263238';
  ctx.lineWidth = 1;
  ctx.font = '10px sans-serif';
  ctx.setLineDash([]);

  [xLeft, xRight].forEach((x) => {
    ctx.beginPath();
    ctx.moveTo(x, yAnchor);
    ctx.lineTo(x, yDim);
    ctx.stroke();
  });

  ctx.beginPath();
  ctx.moveTo(xLeft, yDim);
  ctx.lineTo(xRight, yDim);
  ctx.stroke();

  const ah = 5;
  ctx.beginPath();
  ctx.moveTo(xLeft, yDim);
  ctx.lineTo(xLeft + ah, yDim - 3);
  ctx.lineTo(xLeft + ah, yDim + 3);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(xRight, yDim);
  ctx.lineTo(xRight - ah, yDim - 3);
  ctx.lineTo(xRight - ah, yDim + 3);
  ctx.closePath();
  ctx.fill();

  const tw = ctx.measureText(label).width;
  // 치수선이 기준선보다 아래(시설 하단)면 숫자도 선 아래, 위면 선 위
  const textY = yDim >= yAnchor ? yDim + 12 : yDim - 4;
  ctx.fillText(label, (xLeft + xRight - tw) / 2, textY);
  ctx.restore();
}

export function drawVerticalDimension(
  ctx: CanvasRenderingContext2D,
  yTop: number,
  yBottom: number,
  xAnchor: number,
  xDim: number,
  label: string,
) {
  ctx.save();
  ctx.strokeStyle = '#455a64';
  ctx.fillStyle = '#263238';
  ctx.lineWidth = 1;
  ctx.font = '10px sans-serif';

  [yTop, yBottom].forEach((y) => {
    ctx.beginPath();
    ctx.moveTo(xAnchor, y);
    ctx.lineTo(xDim, y);
    ctx.stroke();
  });

  ctx.beginPath();
  ctx.moveTo(xDim, yTop);
  ctx.lineTo(xDim, yBottom);
  ctx.stroke();

  const ah = 5;
  ctx.beginPath();
  ctx.moveTo(xDim, yTop);
  ctx.lineTo(xDim - 3, yTop + ah);
  ctx.lineTo(xDim + 3, yTop + ah);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(xDim, yBottom);
  ctx.lineTo(xDim - 3, yBottom - ah);
  ctx.lineTo(xDim + 3, yBottom - ah);
  ctx.closePath();
  ctx.fill();

  ctx.save();
  ctx.translate(xDim + 10, (yTop + yBottom) / 2);
  ctx.rotate(-Math.PI / 2);
  const tw = ctx.measureText(label).width;
  ctx.fillText(label, -tw / 2, 0);
  ctx.restore();
  ctx.restore();
}
